import { t, TFunction } from 'i18next'
import { size, isNil, get, set } from 'lodash-es'
import { isSameDay, isAfter } from 'date-fns'
import Promise from 'bluebird'
import { type ValidationErrors } from 'final-form'
import { client } from '@/components/App/client'
import { VALIDATE_FORM_FIELD_ENTRY_UNIQUENESS } from '@/graphql/queries/FormField'

const getErrors = (fields: string[], values: object): ValidationErrors =>
  fields.reduce((acc, name) => {
    const processedValue =
      typeof get(values, name) === 'string'
        ? get(values, name).trim()
        : get(values, name)

    const hasError =
      processedValue instanceof Array
        ? !processedValue.length
        : isNil(processedValue) || processedValue === ''

    if (hasError) {
      set(acc, name, t('This field is required'))
    }

    return acc
  }, {})

export const compose =
  (...validators: any[]): (() => ValidationErrors) =>
  async (value?: string) =>
    Promise.reduce(
      validators,
      async (error, validator) => ({
        ...error,
        ...(await validator(value)),
      }),
      {},
    )

export const required = (fields: string[]) => (values: object) =>
  getErrors(fields, values)

export const singleRequired = (fields: string[]) => (values: object) => {
  const errors = getErrors(fields, values)

  if (size(errors) !== fields.length) {
    return {}
  }

  return errors
}

export const requiredFieldValidator = (value: any) =>
  value ? undefined : t('This field is required')

export const equal =
  (fieldA: string, fieldB: string, msg: string) => (values: object) => {
    const errors: object = {}
    if (values[fieldA] && values[fieldB] && values[fieldA] !== values[fieldB]) {
      errors[fieldA] = msg
    }
    return errors
  }

export const length = (length: number) => (value: string) => {
  const msg = t('This field must contain {{length}} characters', { length })

  return value && String(value).length !== length ? msg : false
}

export const between =
  (a: number, b: number, msgA?: string, msgB?: string) =>
  (value = '') => {
    const messageA =
      msgA || t('This field must be larger than {{value}}', { value: a })
    const messageB =
      msgB || t('This field must be smaller than {{value}}', { value: b })

    const number = parseInt(value, 10)

    if (number <= a) {
      return messageA
    }

    if (number >= b) {
      return messageB
    }

    return false
  }

export const greaterThan =
  (gt = 0) =>
  (value = '0') => {
    if (parseInt(value, 10) <= gt) {
      return `${t('The value must be greater than')} ${gt}`
    }

    return false
  }

export const lessThan =
  (lt = 0) =>
  (value = '0') => {
    if (parseInt(value, 10) >= lt) {
      return `${t('The value must be less than')} ${lt}`
    }

    return false
  }

const validateEmail = (value?: string) =>
  value &&
  // eslint-disable-next-line no-useless-escape
  !/^([a-zA-Z0-9-_\.]+(\.[a-zA-Z0-9-_\.]+)*)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,6}))$/.test(
    value,
  )

const getEmailErrors = (fields: string[], values: object): ValidationErrors =>
  fields.reduce((acc: object, name: string) => {
    const hasError = validateEmail(values[name])

    if (hasError) {
      acc[name] = t('Invalid email address')
    }

    return acc
  }, {})

export const email = (fields: string[]) => (values: object) =>
  getEmailErrors(fields, values)

export const emailSingle = (value?: string) => {
  const hasError = validateEmail(value)
  if (hasError) {
    return t('Invalid email address')
  }
}

const cprRegex =
  /^((((0[1-9]|[12][0-9]|3[01])(0[13578]|10|12)(\d{2}))|(([0][1-9]|[12][0-9]|30)(0[469]|11)(\d{2}))|((0[1-9]|1[0-9]|2[0-8])(02)(\d{2}))|((29)(02)(00))|((29)(02)([2468][048]))|((29)(02)([13579][26])))[-]?\d{4})$/

export const cpr = (value?: string) => {
  if (!!value && !cprRegex.test(value)) {
    return t('Invalid CPR')
  }
}

export const startDate =
  (t: TFunction, msg?: string) =>
  ({ startDate, endDate }: { startDate?: string; endDate?: string }) => {
    if (
      startDate &&
      endDate &&
      isAfter(new Date(startDate), new Date(endDate)) &&
      !isSameDay(new Date(startDate), new Date(endDate))
    ) {
      return {
        startDate: msg || t('Start date must be before the deadline'),
      }
    }

    return false
  }

export const endDate =
  (t: TFunction) =>
  ({ startDate, endDate }: { startDate?: string; endDate?: string }) => {
    if (
      startDate &&
      endDate &&
      isAfter(new Date(startDate), new Date(endDate)) &&
      !isSameDay(new Date(startDate), new Date(endDate))
    ) {
      return {
        endDate: t('Deadline must be after the start date'),
      }
    }

    return false
  }

function FormatFormFieldName(name: string) {
  if (name.includes(':')) {
    return name.split(':')[1]
  }
  if (name.includes('formField__')) {
    return name.split('formField__')[1]
  } else return name
}

export const getFormFieldUniqueErrors = async (
  fields: string[],
  values: object,
): Promise<ValidationErrors> =>
  Promise.reduce(
    fields,
    async (acc: object, name: string) => {
      const validation = await client.query({
        query: VALIDATE_FORM_FIELD_ENTRY_UNIQUENESS,
        //fetchPolicy: 'no-cache',
        variables: {
          formFieldId: FormatFormFieldName(name),
          value: (values[name] || '').toString(),
        },
      })
      // If the value is empty (!!values[name]), then we can ignore validation. If it's both required *and* unique, then the required validator will catch it.
      if (
        !validation?.data?.validateFormFieldEntryUniqueness &&
        !!values[name]
      ) {
        acc[name] = t('This field should be unique.')
      }

      return acc
    },
    {},
  )

export const formFieldUnique = async (fields: string[]) => (values: object) =>
  getFormFieldUniqueErrors(fields, values)

export const engagementBodyLinkPresenceValidation = ({
  emailBody,
  smsBody,
  channel,
}: {
  emailBody?: string
  smsBody?: string
  channel: 'EMAIL' | 'SMS'
}): ValidationErrors => {
  const bodyToCheck = channel === 'EMAIL' ? emailBody : smsBody

  if (
    !/(<span.+?data-value="link"[^>]*>.+?(?=<)<\/span>)/g.test(
      bodyToCheck ?? '',
    )
  ) {
    return {
      [channel === 'EMAIL' ? 'emailBody' : 'smsBody']: t(
        'The body must contain a link variable',
      ),
    }
  }
}

export const regexValidation =
  (regexString: string, message: string) => (value: string) => {
    try {
      const regex = new RegExp(regexString)
      if (!regex.test(value ?? '')) {
        return message
      }
    } catch (e) {
      return 'Invalid regex'
    }
  }

export function CheckEmploymentDates(values: object) {
  const employmentStart = values['employment_start'] || ''
  const employmentEnd = values['employment_end'] || ''
  if (!employmentStart || !employmentEnd) {
    return
  }
  return ['employment_start', 'employment_end'].reduce(
    (acc: object, name: string) => {
      if (name === 'employment_start' && employmentStart > employmentEnd) {
        acc[name] = t(
          'Employment start date cannot be after employment end date',
        )
      } else if (name === 'employment_end' && employmentEnd < employmentStart) {
        acc[name] = t(
          'Employment end date cannot be before employment start date',
        )
      }
      return acc
    },
    {},
  )
}
